import * as React from "react";
import {
    List,
    Show,
    Edit,
    Datagrid,
    SimpleShowLayout,
    SimpleForm,
    TextField,
    EmailField,
    DateField,
    TextInput,
    DateInput,
    BooleanField,
    NumberField,
    ArrayField,
    ReferenceField,

    TabbedShowLayout,
    Tab,
    BooleanInput,
    Create,
    PasswordInput,
    NumberInput,
    ImageField,
    useRecordContext,
    FileField,
    Button,
    sanitizeFieldRestProps,
    WrapperField,
    useRefresh,
    useUpdate,
    SingleFieldList,
    FunctionField,
    Toolbar,
    SaveButton,
    TabbedForm,
    ArrayInput,
    ReferenceArrayInput,
    SelectArrayInput,
    InfiniteList,
    EditButton,
    SelectInput,
    ReferenceInput,
    AutocompleteInput,
    SimpleFormIterator,
    FormDataConsumer,
    ReferenceManyField,
    useChoicesContext,
    AutocompleteArrayInput,
    downloadCSV,
} from 'react-admin';
import { useController, useForm } from "react-hook-form";
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import get from 'lodash/get';
import { Typography, Link } from '@mui/material';
import { apiUrl, authenticatedApi } from "../dataProvider";
import { designations, getDesignations, getStatuses, objectToSelectChoices, stateAbbrToName, states, toSelectChoices } from "../helpers/strings";
import { UserList } from "./Users";
import { ReferenceManyInput, ReferenceManyToManyInput } from "@react-admin/ra-relationships";
import { TabCount } from "../Components/TabCount";
import { FormEntryFrontendButton, FormEntryUpdateStatusButton } from "./FormEntries";
import { GenericOptionRenderer, UserOptionRenderer } from "../Components/OptionRenderer";
import jsonExport from 'jsonexport/dist';

const listFilters = [
    <TextInput source="q" label="Search" alwaysOn />,
    // <BooleanInput source="active" label="Is Active?" />,
    // behind_billing would rely on maestro having crud hooks which we don't have
    // <BooleanInput source="behind_billing" label="Is Billing Behind?" />,
    <SelectInput source="status" label="Entry Status" choices={[
        // {id: null, name: 'All'},
        {id: 'not-started', name: 'Not Started'},
    ]}/>
];

const communityExporter = (communities) => {
    const dataForExport = communities.map(community => {
        return {
            'Member Number': community.member_number,
            Name: community.name,
            City: community.city,
            State: community.state,
            Designation: getDesignations(community.designation ?? null) ?? community.designation,
            'Coordinating Program': community?.coordinating_program?.name ?? '',
        }
    });

    jsonExport(dataForExport, {
        headers: [
            'Member Number',
            'Name',
            'City',
            'State',
            'Designation',
            'Coordinating Program',
        ]
    }, (err, csv) => {
        downloadCSV(csv, 'communities');
    });
};

const CommunityList = (props) => (
    <InfiniteList {...props} sort={{ field: 'state', order: 'ASC' }} filters={listFilters} exporter={communityExporter} hasCreate>
        <Datagrid rowClick="show">
            <TextField source="name" sortable={false} />
            <TextField source="city" sortable={false} />
            <FunctionField label="State" source="state" sortable={false} render={record => stateAbbrToName(record.state)} />
            {/* <TextField source="zip" /> */}

            <FunctionField label="Current Designation" source="designation" sortable={false} render={record => getDesignations(record.designation ?? null)} />

            <EditButton/>
        </Datagrid>
    </InfiniteList>
);

const CommunityShow = (props) => {
    const refresh = useRefresh();
    return (
    <Show {...props} title={'View Community'}>
        <TabbedShowLayout>
            <Tab label="Community Info">
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <SimpleShowLayout>
                            <TextField source="name" />
                            <TextField source="coordinating_program.name"/>
                            <FunctionField label="Current Designation" source="designation" render={record => getDesignations(record.designation ?? null)} />
                            <TextField source="member_number" />
                        </SimpleShowLayout>
                    </Grid>
                    <Grid item xs={6}>
                        <SimpleShowLayout>
                            <TextField source="phone" />
                            <WrapperField label="Address" sortable={false}>
                                <TextField source="address" />
                                <br/>
                                <TextField source="city" />, <FunctionField label="State" source="state" render={record => stateAbbrToName(record.state)} /> <TextField source="zip" />
                            </WrapperField>
                        </SimpleShowLayout>
                    </Grid>
                </Grid>
            </Tab>
            <Tab label="Users" count={<TabCount source="users" />}>
                <ArrayField source="users" label={false}>
                    <Datagrid rowClick={(id, resource, record) => '/users/'+id+'/show'} bulkActionButtons={false} sort={{field: 'name'}}>
                        <TextField sortable={false} source="name" />
                        <TextField sortable={false} source="email" label="Email" />
                    </Datagrid>
                </ArrayField>
            </Tab>
            <Tab label="Forms" count={<TabCount source="forms" />}>
                <ArrayField source="forms" label={false}>
                    <Datagrid rowClick={(id, resource, record) => '/forms/'+id+'/show'} bulkActionButtons={false} sort={{field: 'name'}}>
                        <TextField sortable={false} source="name" />
                    </Datagrid>
                </ArrayField>
            </Tab>
            <Tab label="Form Entries" count={<TabCount source="entries" />}>
                <ArrayField source="entries" label={false}>
                    <Datagrid rowClick={(id, resource, record) => '/form-entries/'+id+'/show'} bulkActionButtons={false}>
                        <TextField source="form.name" label="Form" />
                        {/* <DateField source="created_at" label="Submitted Date" /> */}
                        <FunctionField label="Status" source="status" render={record => getStatuses(record.status)} />
                        <FunctionField label="Archived Designation" source="archived_designation" render={record => getDesignations(record.archived_designation ?? null)} />

                        {/* Todo: This doesn't update the DataGrid row */}
                        <FormEntryUpdateStatusButton onChange={()=> {setTimeout(() => {refresh()}, 5)}} />
                        <FormEntryFrontendButton />
                    </Datagrid>
                </ArrayField>
            </Tab>
        </TabbedShowLayout>
    </Show>
)};

const EditToolbar = props => (
    <Toolbar {...props} >
        <SaveButton />
    </Toolbar>
);

const CommunityEdit = (props) => (
    <Edit {...props} title={'Edit Community'} redirect="show" mutationMode="pessimistic">
        <TabbedForm>
            <TabbedForm.Tab label="Community Info">
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Stack>
                            <TextInput source="name" isRequired={true} />
                            <ReferenceInput source="coordinating_program_id" perPage={-1} reference="coordinating-programs">
                                <AutocompleteInput optionText="name" />
                            </ReferenceInput>
                            <SelectInput source="designation" emptyText={'Choose...'} choices={toSelectChoices(designations)} />
                            <TextInput source="member_number" />
                        </Stack>
                    </Grid>
                    <Grid item xs={6}>
                        <Stack>
                            <TextInput source="phone" />
                            <TextInput source="address" />
                            <TextInput source="city" />
                            <AutocompleteInput source="state" choices={objectToSelectChoices(states)} />
                            <TextInput source="zip" />
                        </Stack>
                    </Grid>
                </Grid>
            </TabbedForm.Tab>
            <TabbedForm.Tab label="Users">
                <em>Note: please create new users under the "Users" section before attempting to add them below.</em>

                <ReferenceManyToManyInput reference="users" through="community-user" using="community_id,user_id" perPage={-1} perPageChoices={-1}>
                    <AutocompleteArrayInput label="User" optionText={<UserOptionRenderer/>} inputText={choice => choice.name} />
                </ReferenceManyToManyInput>
            </TabbedForm.Tab>
            <TabbedForm.Tab label="Forms">
                <ReferenceManyToManyInput reference="forms" through="community-form" using="community_id,form_id" perPage={-1} perPageChoices={-1}>
                    <AutocompleteArrayInput label="User" optionText={<GenericOptionRenderer/>} inputText={choice => choice.name} />
                </ReferenceManyToManyInput>
            </TabbedForm.Tab>
        </TabbedForm>
    </Edit>
);

const CommunityCreate = props => (
    <Create {...props} redirect="show">
        <TabbedForm>
            <TabbedForm.Tab label="Community Info">
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Stack>
                            <TextInput source="name" isRequired={true} />
                            <ReferenceInput source="coordinating_program_id" perPage={-1} reference="coordinating-programs">
                                <AutocompleteInput optionText="name" />
                            </ReferenceInput>
                            <SelectInput source="designation" emptyText={'Choose...'} choices={toSelectChoices(designations)} />
                            <TextInput source="member_number" />
                        </Stack>
                    </Grid>
                    <Grid item xs={6}>
                        <Stack>
                            <TextInput source="phone" />
                            <TextInput source="address" />
                            <TextInput source="city" />
                            <AutocompleteInput source="state" choices={objectToSelectChoices(states)} />
                            <TextInput source="zip" />
                        </Stack>
                    </Grid>
                </Grid>
            </TabbedForm.Tab>
            <TabbedForm.Tab label="Users">
                <em>Note: please create new users under the "Users" section before attempting to add them.</em>

                <ReferenceManyToManyInput reference="users" through="community-user" using="community_id,user_id" perPage={-1} perPageChoices={-1}>
                    <AutocompleteArrayInput label="User" optionText={<UserOptionRenderer/>} inputText={choice => choice.name} />
                </ReferenceManyToManyInput>
            </TabbedForm.Tab>
            <TabbedForm.Tab label="Forms">
                <ReferenceManyToManyInput reference="forms" through="community-form" using="community_id,form_id" perPage={-1} perPageChoices={-1}>
                    <AutocompleteArrayInput label="User" optionText={<GenericOptionRenderer/>} inputText={choice => choice.name} />
                </ReferenceManyToManyInput>
            </TabbedForm.Tab>
        </TabbedForm>
    </Create>
);

export {CommunityList, CommunityShow, CommunityEdit, CommunityCreate};
